const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

// Every /api/v1/swap route is gated on the x-patrol-id header (patrolAuth in the
// api). Swap sellers are not core members, so the patrol comes from build-time
// config (REACT_APP_PATROL_ID) rather than a user profile; deploying swap for
// another patrol is a config change, not a code change.
const rawPatrolId = process.env.REACT_APP_PATROL_ID;
const patrolId = typeof rawPatrolId === 'string' ? rawPatrolId.trim() : '';
const hasPatrolId = Boolean(patrolId) && patrolId !== 'undefined' && patrolId !== 'null';

export const PATROL_ID_NOT_CONFIGURED = 'This site is not configured for a patrol (missing patrol ID). Please contact the swap organizers.';

// Server error codes from patrolAuth, in words a seller can act on.
const ERROR_MESSAGES = {
    'patrol/missing-patrol-id': PATROL_ID_NOT_CONFIGURED,
    'patrol/not-a-member': 'Your account is not a member of this patrol, so you cannot use the swap. Please contact the swap organizers.',
    'patrol/account-disabled': 'Your account has been disabled. Please contact the swap organizers.',
    'patrol/membership-check-failed': 'We could not verify your patrol membership right now. Please try again in a few minutes.'
};

const readableError = (code) => ERROR_MESSAGES[ code ] || code;

// All swap calls go through here so none can forget the header. If no patrol id
// is configured we do NOT send the request: an empty or "undefined" header is
// either rejected (400) or, worse, accepted as a nonexistent patrol. The throw
// is caught by each caller's try/catch and returned as { error }. Never retried.
const swapFetch = (url, payload) => {
    if (!hasPatrolId) {
        return Promise.reject(new Error(PATROL_ID_NOT_CONFIGURED));
    }

    return fetch(url, { ...payload, headers: { ...payload.headers, 'x-patrol-id': patrolId } });
};

export const getUser = async (uid, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/users/${ uid }`, payload);
        const json = response && await response.json();

        if (response.ok) {
            return json;
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else {
            return null;
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const getAllUsers = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/users`, payload);
        const json = response && await response.json();

        if (response.ok) {
            return json;
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else {
            return [];
        }   
    } catch (error) {
        return { error: error.message };
    }
};

export const addUser = async (userData, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(userData)
        };  

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/users`, payload);
        
        if (response.ok) {
            const location = response.headers.get('Location');
            const id = location.split('/').pop();

            return { id };
        } else {
            const json = response && await response.json();

            return { error: readableError(json?.error) || 'Failed to add user' };
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const updateUser = async (userData, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                "Authorization": `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(userData)
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/users/${ userData.id }`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json();

            return { error: readableError(json.error) || 'Failed to update user' };
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const getAllItems = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                "Authorization": `Bearer ${ token }`
            }
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/items`, payload);
        const json = response && await response.json();

        if (!response.ok) {
            return { error: readableError(json?.error) || 'Failed to fetch items' };
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else if (!Array.isArray(json)) {
            return [];
        }

        return json;
    } catch (error) {
        return { error: error.message };
    }
};

export const getItemsBySeller = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                "Authorization": `Bearer ${ token }`
            }
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/items/seller/${ id }`, payload);
        const json = response && await response.json();

        if (!response.ok) {
            return { error: readableError(json?.error) || 'Failed to fetch items' };
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else if (!Array.isArray(json)) {
            return [];
        }

        return json;
    } catch (error) {
        return { error: error.message };
    }
};

export const getItem = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                "Authorization": `Bearer ${ token }`
            }
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/items/${ id }`, payload);
        const json = response && await response.json();

        if (!response.ok) {
            return { error: readableError(json?.error) || 'Failed to fetch item' };
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else {
            return json;
        }
    } catch (error) {
        return { error: error.message };
    }
};

// addItem/updateItem answer 201 with the item id in the Location header. Turn
// anything else into an { error } instead of letting a null header throw, which
// would hide the real problem behind "Cannot read properties of null".
const idFromItemResponse = async (response) => {
    if (!response.ok) {
        const json = await response.json().catch(() => null);

        return { error: readableError(json?.error) || `Request failed with status ${ response.status }` };
    }

    const location = response.headers.get('Location');

    if (!location) {
        return { error: 'The server did not return the item id' };
    }

    return location.split('/').pop();
};

export const addItem = async (data, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: data && JSON.stringify(data)
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/items`, payload);

        return await idFromItemResponse(response);
    } catch (error) {
        return { error: error.message };
    }
};

export const updateItem = async (data, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                "Authorization": `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: data && JSON.stringify(data)
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/items/${ data.id }`, payload);

        return await idFromItemResponse(response);
    } catch (error) {
        return { error: error.message };
    }
};

export const deleteItems = async (ids, token) => {
    try {
        const idsString = Array.isArray(ids) ? ids.join(',') : ids;
        const payload = {
            method: 'DELETE',
            headers: {
                "Authorization": `Bearer ${ token }`
            }
            // No body or Content-Type needed
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/items/delete?ids=${ encodeURIComponent(idsString) }`, payload);

        if (!response.ok) {
            const json = await response.json().catch(() => null);

            return { error: readableError(json?.error) || 'Failed to remove items' };
        }

        return { success: true };
    } catch (error) {
        return { error: error.message };
    }
};

export const verifyItems = async (items, adminUID, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                "Authorization": `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items: items.map(item => ({ id: item.id, verifiedBy: adminUID })) })
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/items/verify`, payload);

        if (!response.ok) {
            const json = await response.json();

            if (json && json.error) {
                return { error: readableError(json.error) };
            }

            return { error: 'Failed to verify items' };
        }

        return true;
    } catch (error) {
        return { error: error.message };
    }
};

export const payoutSeller = async (items, adminId, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items, adminId })
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/sellers/payout`, payload);

        if (!response.ok) {
            const json = await response.json();

            if (json && json.error) {
                return { error: readableError(json.error) };
            }

            return { error: 'Failed to payout seller' };
        }

        // Return the actual response data which includes payout amount
        const json = await response.json();

        return json;
    } catch (error) {
        return { error: error.message };
    }
};

export const disableUser = async (uid, token) => {
    try {
        const payload = {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ token }` }
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/users/${ uid }`, payload);
        const json = response && await response.json();

        if (response.ok) {
            return json;
        } else {
            return { error: readableError(json.error) || 'Failed to disable user' };
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const uploadPhoto = async (id, photo, token) => {
    try {
        const formData = new FormData();

        formData.append('id', id);
        formData.append('photo', photo);

        const payload = {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${ token }` },
            body: formData
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/items/${ id }/photo`, payload);

        if (response.ok) {
            const json = await response.json();

            return json.photoURL;
        } else {
            const json = await response.json();

            return { error: readableError(json.error) };
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const verifyPhone = async (uid, method, code, identifier, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ code: code, identifier: identifier })
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/notify/verify/${ uid }/${ method }`, payload);

        if (response.ok) {
            const json = await response.json();

            if (json.verified) {
                return ({ verified: true });
            } else if (json.sent) {
                return ({ sent: true });
            } else {
                return ({ error: 'Unknown verification status.' });
            }
        } else {
            const json = await response.json();

            return { error: readableError(json.error) };
        }
    } catch (error) {
        return { error: error.message };
    }
};



export const callSubmitGitHubIssue = async (email, type, desc, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(
                {
                    title: `${ email } - ${ type }`,
                    body: desc,
                    labels:["core"]
                }
            )
        };

        const response = await fetch(`${ apiEndpoint }/api/submit-github-issue`, payload);

        return response.ok;
    } catch (error) {
        return { error: error.message };
    }
};

export const processPayment = async (paymentData, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ ...paymentData, donationAmount: paymentData.donationAmount || 0 })
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/payments`, payload);
        const json = response && await response.json();

        if (response.ok) {
            return json;
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else {
            return { error: 'Payment processing failed' };
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const processSale = async (saleData, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(saleData)
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/sales`, payload);
        const json = response && await response.json();

        if (response.ok) {
            return json;
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else {
            return { error: 'Sale processing failed' };
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const getSale = async (saleId, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/sales/${ saleId }`, payload);
        const json = response && await response.json();

        if (response.ok) {
            return json;
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else {
            return { error: 'Failed to get sale' };
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const getAllSales = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/sales`, payload);
        const json = response && await response.json();

        if (response.ok) {
            return json;
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else {
            return { error: 'Failed to get sales' };
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const getFailedSales = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/sales/failed`, payload);
        const json = response && await response.json();

        if (response.ok) {
            return json;
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else {
            return { error: 'Failed to get failed sales' };
        }
    } catch (error) {
        return { error: error.message };
    }
};

export const retryFailedSale = async (saleId, retryData, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(retryData)
        };

        const response = await swapFetch(`${ apiEndpoint }/api/v1/swap/sales/${ saleId }/retry`, payload);
        const json = response && await response.json();

        if (response.ok) {
            return json;
        } else if (json && json.error) {
            return { error: readableError(json.error) };
        } else {
            return { error: 'Retry failed' };
        }
    } catch (error) {
        return { error: error.message };
    }
};